import { useState } from 'react';
import { addDays, todayIn } from '@njpos/core';
import { api } from '../lib/api';
import { ErrorNote, Modal, useLoad } from '../components/ui';

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
// <input type="datetime-local"> works in local time without a zone
const toLocal = (iso: string) => { const d = new Date(iso); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };

export default function TimeClock() {
  const today = todayIn();
  const [from, setFrom] = useState(addDays(today, -6));
  const [to, setTo] = useState(today);
  const data = useLoad(() => api<{ entries: any[]; totals: any[] }>(`/api/timeclock/entries?from=${from}&to=${to}`), [from, to]);
  const mine = useLoad(() => api<{ open: any }>('/api/timeclock/status'), []);
  const [edit, setEdit] = useState<any>(null);
  const [err, setErr] = useState<unknown>(null);
  async function toggle() {
    setErr(null);
    try { await api(`/api/timeclock/${mine.data?.open ? 'out' : 'in'}`, { body: {} }); mine.reload(); data.reload(); } catch (e) { setErr(e); }
  }
  return (
    <div className="stack">
      <div className="page-head">
        <div className="grow"><h1>Time clock</h1><p>Hours worked at this store. Staff clock in and out from the register.</p></div>
        <label>From<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label>To<input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <button className={mine.data?.open ? '' : 'primary'} onClick={toggle}>{mine.data?.open ? 'Clock out' : 'Clock in'}</button>
      </div>
      <ErrorNote error={data.error ?? err} />
      <div className="panel"><div className="panel-head"><h2>Hours by person</h2></div>
        <table className="grid"><thead><tr><th>Person</th><th className="n">Hours</th><th /></tr></thead>
          <tbody>{data.data?.totals.map((t) => <tr key={t.userId}><td>{t.name}</td><td className="n">{t.hours.toFixed(2)}</td><td>{t.open && <span className="chip good">On the clock</span>}</td></tr>)}</tbody>
        </table>
        {data.data?.totals.length === 0 && <div className="empty">No shifts in this period.</div>}
      </div>
      <div className="panel"><div className="panel-head"><h2>Shifts</h2></div>
        <table className="grid"><thead><tr><th>Person</th><th>In</th><th>Out</th><th className="n">Hours</th><th /></tr></thead>
          <tbody>{data.data?.entries.map((e) => (
            <tr key={e.id} className="clickable" onClick={() => setEdit(e)}>
              <td>{e.name}</td><td>{fmt(e.clock_in)}</td><td>{e.clock_out ? fmt(e.clock_out) : <span className="chip good">Open</span>}</td>
              <td className="n">{e.hours.toFixed(2)}</td><td>{e.edited && <span className="chip warn">Edited</span>}</td>
            </tr>))}</tbody>
        </table>
      </div>
      {edit && <EditShift entry={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); data.reload(); }} />}
    </div>
  );
}

function EditShift({ entry, onClose, onDone }: { entry: any; onClose: () => void; onDone: () => void }) {
  const [inAt, setIn] = useState(toLocal(entry.clock_in));
  const [outAt, setOut] = useState(entry.clock_out ? toLocal(entry.clock_out) : '');
  const [note, setNote] = useState(entry.note ?? '');
  const [err, setErr] = useState<unknown>(null);
  return (
    <Modal title={`Edit shift · ${entry.name}`} onClose={onClose} footer={<button className="primary" onClick={async () => {
      try {
        await api(`/api/timeclock/entries/${entry.id}`, { method: 'PATCH', body: { clockIn: new Date(inAt).toISOString(), clockOut: outAt ? new Date(outAt).toISOString() : null, note: note || null } });
        onDone();
      } catch (e) { setErr(e); }
    }}>Save</button>}>
      <ErrorNote error={err} />
      <div className="field-row">
        <label>Clock in<input type="datetime-local" value={inAt} onChange={(e) => setIn(e.target.value)} /></label>
        <label>Clock out<input type="datetime-local" value={outAt} onChange={(e) => setOut(e.target.value)} /></label>
      </div>
      <label>Note<input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this changed" /></label>
      <p className="faint">Edits are recorded in the audit log.</p>
    </Modal>
  );
}
